'use client';

import Box from '@mui/material/Box';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import { forwardRef, type MouseEvent, type ReactNode } from 'react';
import { fontFamilies } from '../../theme/typography';
import { composeSx } from '../../utils/styledUtils';
import { GlassTooltip } from '../GlassTooltip';

export type InlineTagChipSeverity = 'neutral' | 'success' | 'warning' | 'error' | 'info' | 'primary';

export interface InlineTagChipProps {
  /** The label / key rendered in monospace. */
  label: ReactNode;
  /** Optional leading icon (rendered before the label). Keep to ~12px. */
  icon?: ReactNode;
  /** Tooltip content shown on hover. */
  tooltip?: ReactNode;
  /** Severity color. Default 'primary' (matches brand). */
  severity?: InlineTagChipSeverity;
  /** Click handler — receives the click event. When provided, chip becomes a button. */
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  /** Anchor href — when provided, chip becomes an anchor. Mutually exclusive with onClick. */
  href?: string;
  /** Sx overrides. */
  sx?: SxProps<Theme>;
  /** Optional aria label override. Defaults to the label string. */
  ariaLabel?: string;
}

function severityColors(theme: Theme, severity: InlineTagChipSeverity) {
  const isDark = theme.palette.mode === 'dark';
  if (severity === 'neutral') {
    return {
      bg: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
      border: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)',
      text: theme.palette.text.secondary,
      hoverBg: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
    };
  }
  const palette = severity === 'primary' ? theme.palette.primary : theme.palette[severity];
  return {
    bg: alpha(palette.main, isDark ? 0.12 : 0.08),
    border: alpha(palette.main, isDark ? 0.32 : 0.24),
    text: isDark ? palette.light : palette.dark,
    hoverBg: alpha(palette.main, isDark ? 0.2 : 0.14),
  };
}

export const InlineTagChip = forwardRef<HTMLElement, InlineTagChipProps>(function InlineTagChip(
  { label, icon, tooltip, severity = 'primary', onClick, href, sx, ariaLabel },
  ref,
) {
  const theme = useTheme();
  const colors = severityColors(theme, severity);

  if (process.env['NODE_ENV'] !== 'production' && href && onClick) {
    // biome-ignore lint/suspicious/noConsole: dev-only warning for an API misuse the type system can't catch
    console.warn(
      '[InlineTagChip] `onClick` and `href` are mutually exclusive. `href` will be used; `onClick` is ignored.',
    );
  }

  const interactive = Boolean(onClick) || Boolean(href);
  const component: 'a' | 'button' | 'span' = href ? 'a' : onClick ? 'button' : 'span';

  const chip = (
    <Box
      ref={ref}
      component={component as 'span'}
      {...(href ? { href } : {})}
      {...(onClick && !href ? { onClick, type: 'button' as const } : {})}
      aria-label={ariaLabel ?? (typeof label === 'string' ? label : undefined)}
      sx={composeSx(
        {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          height: 18,
          px: 0.75,
          py: 0,
          verticalAlign: 'baseline',
          borderRadius: '4px',
          backgroundColor: colors.bg,
          border: `1px solid ${colors.border}`,
          color: colors.text,
          fontFamily: fontFamilies.mono,
          fontSize: '0.6875rem',
          fontWeight: 500,
          letterSpacing: '0.01em',
          lineHeight: 1,
          whiteSpace: 'nowrap',
          textDecoration: 'none',
          cursor: interactive ? 'pointer' : 'default',
          transition: 'background-color 120ms ease, border-color 120ms ease',
          ...(interactive && {
            '&:hover': {
              backgroundColor: colors.hoverBg,
            },
            '&:focus-visible': {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 1,
            },
          }),
        },
        sx,
      )}
    >
      {icon && (
        <Box
          component="span"
          aria-hidden
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.75rem',
            lineHeight: 1,
          }}
        >
          {icon}
        </Box>
      )}
      <Box component="span" sx={{ display: 'inline-block' }}>
        {label}
      </Box>
    </Box>
  );

  if (tooltip != null && tooltip !== '') {
    return (
      <GlassTooltip title={tooltip} placement="top">
        {chip}
      </GlassTooltip>
    );
  }

  return chip;
});

InlineTagChip.displayName = 'InlineTagChip';

export default InlineTagChip;
